import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface KeywordSynonym {
  id: string;
  keyword_id: string;
  synonym: string;
}

export function useKeywordSynonyms() {
  const [synonyms, setSynonyms] = useState<KeywordSynonym[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSynonyms();

    const channel = supabase
      .channel('keyword-synonyms-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'keyword_synonyms' },
        fetchSynonyms,
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);

  async function fetchSynonyms() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('keyword_synonyms')
        .select('*');

      if (error) throw error;
      setSynonyms(data || []);
    } catch (error) {
      console.error('Error fetching keyword synonyms:', error);
    } finally {
      setLoading(false);
    }
  }

  async function addSynonym(keywordId: string, synonym: string) {
    try {
      const { data, error } = await supabase
        .from('keyword_synonyms')
        .insert([{ keyword_id: keywordId, synonym }])
        .select();

      if (error) throw error;
      return data?.[0] || null;
    } catch (error) {
      console.error('Error adding synonym:', error);
      return null;
    }
  }

  async function removeSynonym(synonymId: string) {
    try {
      const { error } = await supabase
        .from('keyword_synonyms')
        .delete()
        .eq('id', synonymId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error removing synonym:', error);
      return false;
    }
  }

  async function findKeywordBySynonym(searchTerm: string) {
    try {
      // First check exact match
      const { data: exactMatch, error: exactError } = await supabase
        .from('keyword_synonyms')
        .select('keyword_id')
        .eq('synonym', searchTerm.toLowerCase().trim());

      if (exactError) throw exactError;

      if (exactMatch && exactMatch.length > 0) {
        return exactMatch[0].keyword_id;
      }

      // Then check partial match
      const { data: partialMatch, error: partialError } = await supabase
        .from('keyword_synonyms')
        .select('keyword_id')
        .ilike('synonym', `%${searchTerm.toLowerCase().trim()}%`);

      if (partialError) throw partialError;

      if (partialMatch && partialMatch.length > 0) {
        return partialMatch[0].keyword_id;
      }

      return null;
    } catch (error) {
      console.error('Error finding keyword by synonym:', error);
      return null;
    }
  }

  async function getSynonymsForKeyword(keywordId: string) {
    try {
      const { data, error } = await supabase
        .from('keyword_synonyms')
        .select('*')
        .eq('keyword_id', keywordId);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching synonyms for keyword:', error);
      return [];
    }
  }

  return {
    synonyms,
    loading,
    addSynonym,
    removeSynonym,
    findKeywordBySynonym,
    getSynonymsForKeyword,
  };
}
